<template>
  <div class="donut-chart" :class="className" v-inview="{ callback: onInview }">
    <h3 class="donut-chart__title">未投票數</h3>
    <div class="donut-chart__layout">
      <svg ref=donutChart width="400" height="400" viewBox="0 0 400 400"></svg>
      <div class="donut-chart__infos">
        <div class="donut-chart__infos--layout">
          <div class="donut-chart__info">
            <div class="donut-chart__info--label" :style="backgroundStyle">未投票數</div>
            <div class="donut-chart__info--number">{{ display | numeric }}</div>
          </div>

          <div class="donut-chart__info">
            <div class="donut-chart__info--label" :style="backgroundStyle">未投票率</div>
            <div class="donut-chart__info--number">{{ values[1] | percentage }}</div>
          </div>

          <div class="donut-chart__comparison">
            <div class="donut-chart__comparison--mark" :class="markClassName" :style="{ 'border-bottom': `8px solid ${themeColor}` }"></div>
            <div class="donut-chart__comparison--value" :style="colorStyle">{{ comparison | percentage }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3'

const TEXT_COLOR = ['#c6c6c6', '#1a1a1a']

export default {
  name: 'DonutChart',
  data () {
    return {
      isInview: false,
      svg: null,
      attrWidth: 0,
      attrHeight: 0,
      g: null,
      pie: null,
      arc: null,
      textLabel: null,
      dataReady: null,
      posA: null,
      posB: null,
      posC: null,
      elWidth: 0,
      elHeight: 0,
      radius: 0
    }
  },
  props: {
    themeColor: { type: String, default: '#FFCA69' },
    display: { type: Number, required: true },
    comparison: { type: Number, required: true },
    values: { type: Array, required: true },
    colors: { type: Array, required: true },
    labels: { type: Array, required: true }
  },
  computed: {
    className () {
      return {
        'donut-chart--inview': this.isInview
      }
    },
    backgroundStyle () {
      const background = this.colors[1]
      return {
        background
      }
    },
    colorStyle () {
      const color = this.colors[1]
      return {
        color
      }
    },
    markClassName () {
      return {
        increase: this.comparison > 0,
        decrease: this.comparison < 0
      }
    },
    textScale () {
      return Math.round(320 / this.elHeight * 100) / 100
    },
    innerRadiusScale () {
      return ((this.elHeight - 10 * 2) / 2) * ((Math.round(this.attrHeight / this.elHeight * 100) / 100)) - 50
    }
  },
  methods: {
    onInview () {
      this.isInview = true
      this.elHeight = this.$el.getBoundingClientRect().height
      this.drawSvg()
      this.drawCircle()
      this.drawText()
      window.addEventListener('resize', () => {
        this.$nextTick(() => {
          this.elHeight = this.$el.getBoundingClientRect().height
          this.modifiedTextPos()
        })
      })
    },
    drawSvg () {
      this.svg = d3.select(this.$refs.donutChart)
      this.attrWidth = this.svg.attr('width')
      this.attrHeight = this.svg.attr('height')
      this.radius = Math.min(this.attrWidth * 0.4, this.attrHeight * 0.4)

      this.g = this.svg
        .append('g')
        .attr('transform', 'translate(' + this.attrWidth * 0.5 + ',' + this.attrHeight * 0.5 + ')'
        )
        .attr('width', this.attrWidth)
        .attr('height', this.attrHeight)
    },
    drawCircle () {
      this.pie = d3.pie()
        .value((d) => {
          return d.value
        })

      let color = d3.scaleOrdinal(this.colors)
      let path = d3.arc().innerRadius(this.radius - 32).outerRadius(this.radius - 45)

      this.dataReady = this.pie(d3.entries(this.values))
      this.arc = this.g
        .selectAll('.arc')
        .data(this.dataReady)
        .enter()
        .append('g')
        .attr('class', 'arc')

      this.arc.append('path')
        .attr('d', path)
        .attr('fill', (d) => {
          return color(d.value)
        })
        .style('stroke-width', '2px')
        .transition()
        .duration((d, i) => {
          return d.value * 1500
        })
        .attrTween('d', (d) => {
          let i = d3.interpolate(d.endAngle, d.startAngle)
          return function (t) {
            d.startAngle = i(t)
            return path(d)
          }
        })
    },
    drawText () {
      let textColor = d3.scaleOrdinal(TEXT_COLOR)
      this.textLabel = d3.arc().innerRadius(this.innerRadiusScale).outerRadius(this.innerRadiusScale + 50)

      this.arc
        .append('text')
        .attr('class', 'text')
        .text((d, index) => {
          return this.labels[index]
        })
        .attr('fill', (d) => {
          return textColor(d.value)
        })

      this.modifiedTextPos()
    },
    modifiedTextPos () {
      this.arc
        .selectAll('text')
        .attr('transform', (d) => {
          let midAngle = (d.startAngle + d.endAngle) / 2
          let flipAngle = midAngle < Math.PI ? 180 : 0
          return `translate(${this.textLabel.centroid(d)})` +
            `rotate(90)` +
            `rotate(${midAngle * 180 / Math.PI + flipAngle})` +
            `scale(${this.textScale})`
        })
    }
  }
}
</script>

<style lang="scss">
.donut-chart {
  $comp: &;
  position: relative;
  max-width: 420px;
  margin: 0 auto;
  text-align: center;
  color: #1a1a1a;
  opacity: 0;
  transform: translateY(10px);
  transition:
    opacity 0.4s ease 0.5s,
    transform 0.7s ease 0.5s;

  &__title {
    margin-bottom: -8%;
    padding-top: 33px;
    color: #1a1a1a;
    font-size: 1.125em;
    font-weight: bold;
    text-align: center;
    line-height: 1em;
    @include rwd($RWD_TABLET_SMALL) {
      font-size: 1.25em;
    }
    @include rwd($RWD_TABLET) {
      margin-bottom: -9%;
      padding-top: 50px;
      font-size: 1.5em;
    }
    @include rwd($RWD_DESKTOP) {
      margin-bottom: -10%;
    }
  }

  &__layout {
    position: relative;
    width: 100%;
    // margin-top: -45px;
    // margin: -45px 0 -35px 0;
    padding-bottom: 100%;
    height: 0;
    display: inline-block;
    vertical-align: middle;
    overflow: hidden;
  }

  &__infos {
    position: absolute;
    display: inline-block;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    @include use-vertical-align();

    &--layout {
      display: inline-block;
      vertical-align: middle;
      // outline: 1px solid red;
    }
  }

  &__info {
    margin-bottom: 13px;

    @include rwd($RWD_TABLET_SMALL) {
      margin-bottom: 20px;
    }

    &--label {
      display: inline-block;
      padding: 0 7px;
      margin-bottom: 3px;
      font-size: 0.875rem;

      @include rwd($RWD_TABLET_SMALL) {
        margin-bottom: 10px;
      }
    }

    &--number {
      font-size: 1.5rem;

      @include rwd($RWD_TABLET_SMALL) {
        font-size: 1.625rem;
      }
    }
  }

  &__info + &__info {
    margin-bottom: 0;
  }

  &__comparison {
    padding-top: 10px;

    &--mark {
      display: none;
      width: 0;
      height: 0;
      margin: 0 5px 2px 0;
      border-left: 5px solid transparent;
      border-right: 5px solid transparent;
      border-bottom: 8px solid #FFCA69;

      &.increase {
        display: inline-block;
      }

      &.decrease {
        display: inline-block;
        transform: rotate(180deg);
      }
    }

    &--value {
      display: inline-block;
      font-size: 1rem;

      @include rwd($RWD_TABLET_SMALL) {
        font-size: 1.125rem;
      }
    }
  }

  &#{$comp}--inview {
    opacity: 1;
    transform: translateY(0);
  }

  svg {
    position: absolute;
    left: 0;
    right: 0;
    margin: 0 auto;
    width: 100%;
    height: auto;
  }

  text {
    font-size: 1.125em;
    text-anchor: middle;
  }
}
</style>
